import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, Button, CircularProgress, Stack, Tooltip,
} from '@mui/material';
import InboxIcon from '@mui/icons-material/Inbox';
import VerifiedIcon from '@mui/icons-material/Verified';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  listCredentialRequests, approveCredentialRequest, rejectCredentialRequest,
} from '../../services/api';

const GREEN = '#76B900';
const STATUS = {
  pending: { color: '#FF9800', label: 'Pending' },
  approved: { color: GREEN, label: 'Issued' },
  rejected: { color: '#9e9e9e', label: 'Rejected' },
};

// Requests inbox — a Noosphere platform user requests a credential from AAM; the
// operator approves, which signs it under AAM's issuer DID.
const Requests = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(new Set());

  const load = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    try { setRows(await listCredentialRequests() || []); }
    catch (e) { /* likely not signed in */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (id, kind) => {
    setBusy((p) => new Set(p).add(id));
    try {
      if (kind === 'approve') await approveCredentialRequest(id);
      else await rejectCredentialRequest(id);
      await load(true);
    } catch (e) { /* noop */ }
    finally { setBusy((p) => { const n = new Set(p); n.delete(id); return n; }); }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;

  const pending = rows.filter((r) => r.status === 'pending').length;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InboxIcon sx={{ color: GREEN }} /> Credential Requests
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Requests from Noosphere platform users · <strong>{pending}</strong> pending
          </Typography>
        </Box>
        <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={() => load()}>Refresh</Button>
      </Stack>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Requester</TableCell>
              <TableCell>Subject</TableCell>
              <TableCell>Credential</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => {
              const s = STATUS[r.status] || STATUS.pending;
              return (
                <TableRow key={r.id} hover>
                  <TableCell>{r.requester_name || <span style={{ opacity: 0.5 }}>—</span>}</TableCell>
                  <TableCell><Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{r.subject_did}</Typography></TableCell>
                  <TableCell>{r.credential_type_id}</TableCell>
                  <TableCell align="center">
                    <Chip size="small" icon={r.status === 'approved' ? <VerifiedIcon /> : undefined} label={s.label}
                      sx={{ bgcolor: `${s.color}22`, color: s.color, fontWeight: 700, '& .MuiChip-icon': { color: s.color } }} />
                  </TableCell>
                  <TableCell align="right">
                    {r.status === 'pending' ? (
                      busy.has(r.id) ? <CircularProgress size={16} /> : (
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Tooltip title="Approve — signs the credential under AAM's DID" arrow>
                            <Button size="small" variant="contained" startIcon={<CheckIcon />} onClick={() => act(r.id, 'approve')}
                              sx={{ bgcolor: GREEN, '&:hover': { bgcolor: '#5c8f00' } }}>Approve</Button>
                          </Tooltip>
                          <Button size="small" color="inherit" startIcon={<CloseIcon />} onClick={() => act(r.id, 'reject')}>Reject</Button>
                        </Stack>
                      )
                    ) : <Typography variant="caption" color="text.disabled">{r.decided_at ? new Date(r.decided_at).toLocaleDateString() : ''}</Typography>}
                  </TableCell>
                </TableRow>
              );
            })}
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={5}><Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>No requests yet.</Typography></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default Requests;
